import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Package, ArrowRight, Save, Check, RefreshCw, CreditCard, ExternalLink, Store, Camera, ImageIcon, Upload, Bitcoin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import PushOptIn from "@/components/PushOptIn";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRYPTO_TYPES = ["btc", "eth", "usdt"];

// Same field-card hint as checkout — shown on orders still awaiting a crypto payment
const CryptoPayHint = ({ testid }) => (
  <div data-testid={testid} className="border border-clinical/30 bg-[#0B101C] rounded-lg px-4 py-3.5 space-y-2.5 max-w-md">
    <p className="text-[10px] uppercase tracking-[0.25em] text-clinical flex items-center gap-2">
      <Bitcoin className="h-3.5 w-3.5" strokeWidth={1.5} /> Field card — Crypto 1-2-3
    </p>
    <ol className="text-[11px] text-[#B9BFCE] leading-relaxed space-y-1 font-mono">
      <li>01 — Buy the coin above + $5 extra for gas (SOL or ETH)</li>
      <li>02 — Move it into your Trust Wallet</li>
      <li>03 — Send to the address above FROM Trust Wallet</li>
    </ol>
    <p className="text-[10px] text-[#8E96AB] leading-relaxed">
      Never send direct from CashApp, Venmo, Zelle, or Coinbase.{" "}
      <Link to="/crypto-briefing" className="text-clinical underline underline-offset-4 hover:text-ink transition-colors duration-300">Read the full Crypto Briefing</Link>
    </p>
  </div>
);

const STATUS_TEXT = {
  pending_confirmation: "Pending confirmation",
  confirmed: "Confirmed",
  shipped: "Shipped",
  cancelled: "Cancelled",
  archived: "Archived",
};

function trackingUrl(order) {
  const carrier = (order.shipping_method || "").toLowerCase();
  if (!order.tracking_number) return null;
  const n = encodeURIComponent(order.tracking_number);
  if (carrier.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
  if (carrier.includes("ups")) return `https://www.ups.com/track?tracknum=${n}`;
  if (carrier.includes("usps") || carrier.includes("postal")) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
  if (carrier.includes("dhl")) return `https://www.dhl.com/en/express/tracking.html?AWB=${n}`;
  if (carrier.includes("ontrac")) return `https://www.ontrac.com/tracking/?number=${n}`;
  if (carrier.includes("lasership")) return `https://www.lasership.com/track/${n}`;
  // Universal fallback: 17TRACK covers 900+ carriers worldwide with auto-detect.
  return `https://t.17track.net/en#nums=${n}`;
}
function PreshipPhotos({ order, token }) {
  const id8 = order.id.slice(0, 8);
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!open || src) return;
    let alive = true;
    axios
      .get(`${API}/orders/${order.id}/preship-photo`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then(({ data }) => { if (alive) setSrc(URL.createObjectURL(data)); })
      .catch(() => toast.error("Couldn't load the pre-shipment photo."));
    return () => { alive = false; };
  }, [open, src, order.id, token]);
  if (!order.preship_uploaded_at) return null;
  return (
    <div className="mt-6 border border-[#232B40] p-5 space-y-4" data-testid={`preship-section-${id8}`}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-clinical flex items-center gap-2">
        <Camera className="h-4 w-4" strokeWidth={1.5} /> Pre-shipment photos
      </p>
      {open ? (
        <div className="space-y-3">
          {src ? (
            <img src={src} alt="Pre-shipment photo of your package" data-testid={`preship-image-${id8}`} className="max-h-72 rounded-lg border border-[#232B40] object-contain" />
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-clinical" />
          )}
          <button onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] hover:text-clinical transition-colors duration-300" data-testid={`preship-hide-${id8}`}>Hide photo</button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid={`preship-view-${id8}`}
          className="inline-flex items-center gap-2 border border-[#232B40] text-[#B9BFCE] text-xs px-4 py-2.5 rounded-lg hover:text-clinical hover:border-clinical/40 transition-colors duration-300"
        >
          <ImageIcon className="h-4 w-4" strokeWidth={1.5} /> View photo
        </button>
      )}
    </div>
  );
}

function PaymentDetails({ order, token, onUploaded }) {
  const id8 = order.id.slice(0, 8);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [src, setSrc] = useState(null);
  const paid = order.payment_status === "paid";
  const submitted = Boolean(order.payment_proof_submitted_at);
  const methodLabel = order.payment_option?.label || (order.payment_method === "square" ? "Card via Square" : order.payment_method === "paypal" ? "PayPal" : "Manual");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/orders/${order.id}/payment-proof`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment screenshot submitted — the store has been notified.");
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const view = async () => {
    if (src) { setViewing((v) => !v); return; }
    try {
      const { data } = await axios.get(`${API}/orders/${order.id}/payment-proof`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      setSrc(URL.createObjectURL(data));
      setViewing(true);
    } catch {
      toast.error("Couldn't load the payment screenshot.");
    }
  };

  const statusChip = paid ? (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-clinical border border-clinical/40 bg-clinical/10 px-3 py-1 rounded-full" data-testid={`pay-status-${id8}`}>
      <Check className="h-3 w-3" /> Verified
    </span>
  ) : submitted ? (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-warning border border-warning/40 bg-warning/10 px-3 py-1 rounded-full" data-testid={`pay-status-${id8}`}>
      Submitted
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] border border-[#232B40] px-3 py-1 rounded-full" data-testid={`pay-status-${id8}`}>
      Awaiting screenshot
    </span>
  );

  return (
    <div className="mt-6 border border-[#232B40] p-5 space-y-4" data-testid={`payment-details-${id8}`}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-clinical flex items-center gap-2">
        <CreditCard className="h-4 w-4" strokeWidth={1.5} /> Payment Details
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] mb-1">Method</p>
          <p className="text-ink" data-testid={`pay-method-${id8}`}>{methodLabel}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] mb-1">Amount due</p>
          <p className="text-ink" data-testid={`pay-amount-${id8}`}>${order.total.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] mb-1">Status</p>
          {statusChip}
        </div>
        {submitted && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] mb-1">Submitted</p>
            <p className="text-ink text-xs" data-testid={`pay-submitted-${id8}`}>{new Date(order.payment_proof_submitted_at).toLocaleString()}</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {!paid && order.status !== "cancelled" && (
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`pay-file-${id8}`} onChange={(e) => upload(e.target.files?.[0])} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`pay-upload-${id8}`}
              className="inline-flex items-center gap-2 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] font-semibold px-5 py-2.5 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />}
              {submitted ? "Replace screenshot" : "Upload payment screenshot"}
            </button>
          </>
        )}
        {submitted && (
          <button
            onClick={view}
            data-testid={`pay-view-${id8}`}
            className="inline-flex items-center gap-2 border border-[#232B40] text-[#B9BFCE] text-xs px-4 py-2.5 rounded-lg hover:text-clinical hover:border-clinical/40 transition-colors duration-300"
          >
            <ImageIcon className="h-4 w-4" strokeWidth={1.5} /> {viewing ? "Hide screenshot" : "View screenshot"}
          </button>
        )}
      </div>
      {viewing && src && (
        <img src={src} alt="Your payment screenshot" data-testid={`pay-image-${id8}`} className="max-h-72 rounded-lg border border-[#232B40] object-contain" />
      )}
      {!paid && !submitted && order.status !== "cancelled" && (
        <p className="text-xs text-[#8E96AB] leading-relaxed">
          After you send payment, upload a screenshot here so the store can verify and ship faster.
        </p>
      )}
    </div>
  );
}


function OrderTracker({ order }) {
  const id8 = order.id.slice(0, 8);
  const confirmed = order.status === "confirmed" || order.status === "shipped";
  const paid = order.payment_status === "paid";
  const shipped = order.status === "shipped";
  const stages = [
    { key: "pending", label: "Pending", hint: "Order received — awaiting VetForge confirmation", done: true },
    { key: "confirmed", label: "Confirmed", hint: "Stock confirmed and reserved for you", done: confirmed },
    { key: "paid", label: "Paid", hint: "Payment received — preparing for dispatch", done: paid },
    { key: "shipped", label: "Shipped", hint: "Your order is on its way", done: shipped },
  ];
  const current = shipped ? 3 : paid ? 2 : confirmed ? 1 : 0;
  return (
    <ol className="mt-6" data-testid={`order-tracker-${id8}`}>
      {stages.map((s, idx) => (
        <li key={s.key} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`tracker-stage-${s.key}-${id8}`}>
          {idx < stages.length - 1 && (
            <span className={`absolute left-[11px] top-6 bottom-0 w-px ${stages[idx + 1].done ? "bg-clinical" : "bg-ink/15"}`} />
          )}
          <span
            className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ${
              s.done ? "border-clinical bg-clinical text-cream" : "border-[rgba(200,169,106,0.28)] bg-paper text-[#8E96AB]"
            }`}
          >
            {s.done ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
          </span>
          <div className="pt-0.5">
            <p className={`text-xs uppercase tracking-[0.2em] ${s.done ? "text-ink" : "text-[#8E96AB]"}`}>
              {s.label}
              {idx === current && <span className="ml-2 text-clinical">— current</span>}
            </p>
            <p className="text-[11px] text-[#8E96AB] mt-0.5">{s.hint}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function MyOrders() {
  const { token, user, refreshUser } = useAuth();
  const [orders, setOrders] = useState(null);
  const [profile, setProfile] = useState({ phone: "", street: "", city: "", state: "", zip_code: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    if (user?.profile) setProfile((f) => ({ ...f, ...user.profile }));
  }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.patch(`${API}/auth/profile`, profile, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Details saved — checkout will prefill them.");
      refreshUser();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchOrders = useCallback(
    async (silent = false) => {
      if (!silent) setRefreshing(true);
      try {
        const { data } = await axios.get(`${API}/my-orders`, { headers: { Authorization: `Bearer ${token}` } });
        setOrders(data.sort((a, b) => b.created_at.localeCompare(a.created_at)));
        setLastUpdated(new Date());
      } catch {
        if (!silent) setOrders([]);
      } finally {
        setRefreshing(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    fetchOrders();
    const timer = setInterval(() => fetchOrders(true), 30000);
    return () => clearInterval(timer);
  }, [token, fetchOrders]);

  const payNow = async (orderId) => {
    setPayingId(orderId);
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/square-checkout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (!data?.checkout_url) throw new Error("no checkout url");
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Card checkout is unavailable right now.");
      setPayingId(null);
    }
  };

  return (
    <div data-testid="my-orders-page" className="mx-auto max-w-5xl px-6 sm:px-10 pt-24 pb-32 lg:pt-32">
      <FadeUp className="mb-16 space-y-4">
        <p className="text-xs uppercase tracking-[0.35em] text-clinical">Account — {user?.email}</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter text-ink leading-none">
          My <span className="italic text-[#B9BFCE]">orders.</span>
        </h1>
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB]" data-testid="orders-live-note">
            Live status — updates automatically{lastUpdated ? ` · Last updated ${lastUpdated.toLocaleTimeString()}` : ""}
          </p>
          <button
            data-testid="orders-refresh-button"
            onClick={() => fetchOrders()}
            disabled={refreshing}
            className="inline-flex items-center gap-2 border border-[rgba(200,169,106,0.28)] text-ink text-[10px] uppercase tracking-[0.2em] py-2 px-4 hover:bg-[#1A233A] transition-colors duration-300 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </FadeUp>

      <FadeUp className="mb-16">
        <div className="border border-[#232B40] bg-paper p-6 sm:p-8" data-testid="profile-card">
          <p className="text-xs uppercase tracking-[0.3em] text-clinical mb-2">Saved details</p>
          <p className="text-xs text-[#8E96AB] mb-6">Save your shipping details once — checkout fills them in automatically.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input data-testid="profile-phone-input" placeholder="Phone number" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-11 text-sm" />
            <Input data-testid="profile-street-input" placeholder="Street address" value={profile.street} onChange={(e) => setProfile({ ...profile, street: e.target.value })} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-11 text-sm" />
            <Input data-testid="profile-city-input" placeholder="City" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-11 text-sm" />
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="profile-state-input" placeholder="State" value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-11 text-sm" />
              <Input data-testid="profile-zip-input" placeholder="ZIP" value={profile.zip_code} onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-11 text-sm" />
            </div>
          </div>
          <button
            data-testid="profile-save-button"
            onClick={saveProfile}
            disabled={savingProfile}
            className="mt-4 inline-flex items-center gap-2 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] py-3 px-6 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50"
          >
            {savingProfile ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save details
          </button>
        </div>
      </FadeUp>

      <FadeUp delay={0.05} className="mb-10">
        <PushOptIn />
      </FadeUp>

      {orders === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-clinical" /></div>
      ) : orders.length === 0 ? (
        <FadeUp>
          <div className="border border-[#232B40] bg-paper p-12 text-center space-y-6" data-testid="no-orders-message">
            <Package className="mx-auto h-10 w-10 text-[#8E96AB]" strokeWidth={1.25} />
            <p className="text-sm text-[#B9BFCE]">No orders yet.</p>
            <Link
              to="/products"
              data-testid="browse-products-link"
              className="group inline-flex items-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 px-8 hover:bg-[#DBC28C] transition-colors duration-300"
            >
              Browse the catalog
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      ) : (
        <div className="space-y-6">
          {orders.map((o) => {
            const id8 = o.id.slice(0, 8);
            const tUrl = trackingUrl(o);
            const knownStatus = STATUS_TEXT[o.status] !== undefined;
            return (
              <FadeUp key={o.id}>
                <div className="border border-[#232B40] bg-paper p-6 sm:p-8" data-testid={`my-order-${id8}`}>
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="space-y-1">
                      <p className="font-serif text-xl text-ink" data-testid={`my-order-number-${id8}`}>Order {o.order_number || id8.toUpperCase()}</p>
                      <p className="text-xs text-[#8E96AB] uppercase tracking-[0.15em]">
                        {new Date(o.created_at).toLocaleDateString()} — {o.shipping_method}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-clinical inline-flex items-center gap-1.5" data-testid={`order-vendor-${id8}`}>
                        <Store className="h-3 w-3" /> {o.vendor_name || "VetForge Supply"}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" data-testid={`my-order-method-${id8}`}>
                        {o.payment_method === "square"
                          ? `Card via Square${o.square_fee ? ` (incl. $${o.square_fee.toFixed(2)} processing fee)` : ""}`
                          : o.payment_method === "paypal"
                            ? `PayPal${o.square_fee ? ` (incl. $${o.square_fee.toFixed(2)} processing fee)` : ""}`
                            : o.payment_option
                              ? `${o.payment_option.label} — direct to ${o.vendor_name || "store"}, no fee`
                              : "Manual payment — no fee"}
                      </p>
                      {o.payment_option && o.payment_status !== "paid" && o.status !== "cancelled" && (
                        <div className="mt-2 space-y-3">
                          <div className="flex items-center gap-2 border border-[rgba(200,169,106,0.35)] bg-[#0B101C] rounded-lg px-3 py-2 max-w-md" data-testid={`my-order-pay-${id8}`}>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] shrink-0">Pay ${o.total.toFixed(2)} to:</span>
                            <span className="text-clinical font-mono text-xs break-all flex-1">{o.payment_option.handle}</span>
                            <button data-testid={`my-order-copy-${id8}`} onClick={() => { navigator.clipboard?.writeText(o.payment_option.handle); toast.success("Payment address copied."); }} className="text-[10px] uppercase tracking-[0.15em] text-[#8E96AB] hover:text-clinical border border-[#232B40] rounded-lg px-2.5 py-1 transition-colors duration-300 shrink-0">Copy</button>
                          </div>
                          {o.payment_option.qr && o.vendor_id && (
                            <div className="flex items-start gap-4" data-testid={`my-order-qr-${id8}`}>
                              <img
                                src={`${API}/vendors/${o.vendor_id}/payment-options/${o.payment_option.type}/qr`}
                                alt={`Scan to pay ${o.payment_option.label}`}
                                className="w-28 h-28 rounded-lg border border-[rgba(200,169,106,0.35)] bg-white object-contain p-1.5"
                              />
                              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] leading-relaxed pt-1">
                                Scan to pay<br />or type it manually above
                              </p>
                            </div>
                          )}
                          {CRYPTO_TYPES.includes(o.payment_option.type) && <CryptoPayHint testid={`my-order-crypto-hint-${id8}`} />}
                        </div>
                      )}
                    </div>
                    <div className="text-right space-y-2">
                      <p className="font-serif text-2xl text-clinical">${o.total.toFixed(2)}</p>
                      {o.shipping_cost > 0 && (
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" data-testid={`my-order-shipping-${id8}`}>
                          incl. ${o.shipping_cost.toFixed(2)} shipping
                        </p>
                      )}
                      {o.shipping_perk && (
                        <p className="text-[10px] uppercase tracking-[0.25em] text-clinical" data-testid={`my-order-perk-${id8}`}>
                          {o.shipping_perk}
                        </p>
                      )}
                      <p
                        className={`text-[10px] uppercase tracking-[0.25em] ${o.status === "cancelled" ? "text-[#EF4444]" : o.status === "shipped" || o.status === "confirmed" ? "text-clinical" : "text-warning"}`}
                        data-testid={`my-order-status-${id8}`}
                      >
                        {STATUS_TEXT[o.status] || o.status}
                      </p>
                      <p className={`text-[10px] uppercase tracking-[0.25em] ${o.payment_status === "paid" ? "text-clinical" : "text-[#8E96AB]"}`} data-testid={`my-order-payment-${id8}`}>
                        {o.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </p>
                      {o.payment_method === "square" && o.payment_status !== "paid" && o.status !== "cancelled" && (
                        <button
                          data-testid={`pay-now-${id8}`}
                          onClick={() => payNow(o.id)}
                          disabled={payingId === o.id}
                          className="inline-flex items-center gap-2 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] py-2.5 px-5 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50"
                        >
                          {payingId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                          Pay by card — ${o.total.toFixed(2)}
                        </button>
                      )}
                    </div>
                  </div>

                  {o.status === "cancelled" ? (
                    <div className="mt-6 border border-[#EF4444]/30 bg-[#EF4444]/5 p-5" data-testid={`order-cancelled-${id8}`}>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#EF4444]">Order cancelled</p>
                      <p className="text-xs text-[#B9BFCE] mt-1 leading-relaxed">
                        This order was cancelled by VetForge. If you already paid or have any questions, reply to your confirmation email or reach us via the contact page.
                      </p>
                    </div>
                  ) : (
                    <OrderTracker order={o} />
                  )}
                  {!knownStatus && (
                    <p className="mt-4 text-[11px] uppercase tracking-[0.15em] text-warning" data-testid={`order-issue-${id8}`}>
                      Status: {o.status} — contact VetForge for details.
                    </p>
                  )}

                  <PaymentDetails order={o} token={token} onUploaded={fetchOrders} />
                  <PreshipPhotos order={o} token={token} />

                  {o.status === "shipped" && (
                    <div className="mt-6 border border-clinical/30 bg-clinical/5 p-4" data-testid={`order-tracking-${id8}`}>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-clinical">Shipped via {o.shipping_method}</p>
                      {o.tracking_number ? (
                        <p className="text-sm text-ink mt-1 font-mono flex flex-wrap items-center gap-3">
                          Tracking: {o.tracking_number}
                          {tUrl && (
                            <a href={tUrl} target="_blank" rel="noreferrer" data-testid={`track-link-${id8}`} className="inline-flex items-center gap-1 text-clinical text-xs uppercase tracking-[0.15em] underline underline-offset-4 hover:text-ink transition-colors duration-300">
                              Track package <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-[#8E96AB] mt-1">Tracking number will appear here once added by VetForge.</p>
                      )}
                    </div>
                  )}

                  <div className="mt-6 border-t border-[#232B40] pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {o.items.map((i) => (
                      <div key={i.id} className="text-xs text-[#B9BFCE] flex justify-between gap-4 border border-[#232B40] px-4 py-3">
                        <span>{i.name} ({i.size})</span>
                        <span className="text-ink">×{i.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeUp>
            );
          })}
        </div>
      )}
      <p className="mt-16 pt-6 border-t border-[#232B40] text-[11px] text-[#8E96AB] leading-relaxed" data-testid="orders-policy-note">
        <span className="uppercase tracking-[0.15em] text-ink">All sales are final.</span> A 3.75% processing fee applies to PayPal and all card payments. It is the goal of VetForge Health and Wellness to provide a world class experience — if we do not meet those expectations with an order issue, please allow us the opportunity to reconcile the situation.
      </p>
    </div>
  );
}
